import { Badge, Button, Icon, Link, Spinner, Text } from '@shopify/polaris';
import { ChevronDownMinor, ChevronRightMinor } from '@shopify/polaris-icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import { getProductHistory } from '../../../services/productService';

// Timeline contable de la familia (tab "Historial" de la ficha de producto).
//
// Tallas en columnas, tiempo en filas, reciente → antiguo: el saldo actual va fijo
// arriba y bajar es retroceder en el tiempo. Cada celda muestra el movimiento y,
// debajo en pequeño, el saldo global de la familia tras él. Los eventos neutrales
// (traspasos, devoluciones) mueven mercancía sin cambiar el total: van en gris y
// sin signo. Diseño validado en el mockup "Historial de movimientos".

const MONTHS_ES = [
  'ENERO', 'FEBRERO', 'MARZO', 'ABRIL', 'MAYO', 'JUNIO',
  'JULIO', 'AGOSTO', 'SEPTIEMBRE', 'OCTUBRE', 'NOVIEMBRE', 'DICIEMBRE',
];
const MONTHS_VISIBLE_STEP = 6;

const monthLabel = (key) => {
  const [y, m] = key.split('-');
  return `${MONTHS_ES[parseInt(m, 10) - 1]} ${y}`;
};

const dayLabel = (ev) => {
  if (ev.type === 'SALES') return ev.day.slice(5) ? MONTHS_ES[parseInt(ev.day.slice(5), 10) - 1].slice(0, 3).toLowerCase() : ev.day;
  const [, m, d] = ev.day.split('-');
  return `${parseInt(d, 10)} ${MONTHS_ES[parseInt(m, 10) - 1].slice(0, 3).toLowerCase()}`;
};

const BADGE_STATUS = {
  DELIVERY: 'success',
  OPENING: 'success',
  TRANSFER_GROUP: 'info',
  RETURN: 'warning',
  SALES: undefined, // gris por defecto
  SALE_DIRECT: undefined,
  LOSS: 'critical',
  FOUND: 'attention',
  ADJUST: 'attention',
};

const BADGE_LABEL = {
  DELIVERY: 'Llegada',
  OPENING: 'Apertura',
  TRANSFER_GROUP: 'Traspasos',
  RETURN: 'Devolución',
  SALES: 'Ventas',
  SALE_DIRECT: 'Venta',
  LOSS: 'Extravío',
  FOUND: 'Aparecido',
  ADJUST: 'Regulación',
};

const num = (v) => (v === undefined || v === null || v === 0 ? null : v);

// Filtro de tipos de movimiento de la cabecera. Cada chip agrupa los tipos de evento
// que el usuario entiende como una misma cosa (aparecidos van con extravíos: son las
// dos caras de la 390). Colores de la paleta de badges de Polaris 11.
const TYPE_FILTERS = [
  { key: 'llegadas', label: 'Llegadas', color: '#aee9d1', types: ['DELIVERY', 'OPENING'] },
  { key: 'traspasos', label: 'Traspasos', color: '#a4e8f2', types: ['TRANSFER_GROUP'] },
  { key: 'ventas', label: 'Ventas', color: '#e4e5e7', types: ['SALES', 'SALE_DIRECT'] },
  { key: 'extravios', label: 'Extravíos', color: '#fed3d1', types: ['LOSS', 'FOUND'] },
  { key: 'devoluciones', label: 'Devoluciones', color: '#ffd79d', types: ['RETURN'] },
  { key: 'regulaciones', label: 'Regulaciones', color: '#ffea8a', types: ['ADJUST'] },
];

// Ficha a la que enlaza un evento: las recepciones a su delivery, las devoluciones
// (traspaso único) a su traspaso. Los grupos de traspasos enlazan en el desglose.
const eventUrl = (ev) => {
  if (!ev.refId) return null;
  if (ev.type === 'DELIVERY') return `/deliveries/${ev.refId}`;
  if (ev.type === 'RETURN') return `/transfers/${ev.refId}`;
  if (ev.type === 'SALE_DIRECT') return `/sells/${ev.refId}`;
  return null;
};

// Celda de movimiento: cantidad con signo/color y saldo pequeño debajo.
const MoveCell = ({ qty, saldo, neutral }) => {
  const q = num(qty);
  const color = neutral ? '#6d7175' : q > 0 ? '#007f5f' : '#d72c0d';
  return (
    <div style={{ textAlign: 'center' }}>
      <span style={{ fontSize: 13, fontWeight: 600, color: q === null ? '#c9cccf' : color }}>
        {q === null ? '·' : neutral ? Math.abs(q) : q > 0 ? `+${q}` : q}
      </span>
      {saldo !== undefined && (
        <div style={{ fontSize: 10, color: '#8c9196', marginTop: 1 }}>{saldo}</div>
      )}
    </div>
  );
};

const ProductHistoryTimeline = ({ productId, variants }) => {
  const [expanded, setExpanded] = useState({});
  const [monthsVisible, setMonthsVisible] = useState(MONTHS_VISIBLE_STEP);
  // Tipos activos del filtro de cabecera. Todos encendidos al entrar.
  const [activeFilters, setActiveFilters] = useState(() =>
    TYPE_FILTERS.reduce((acc, f) => ({ ...acc, [f.key]: true }), {}),
  );

  // Scroll infinito: un centinela invisible al pie del listado. Cuando asoma en el
  // viewport se revelan más meses, sin botón. Los datos ya están en memoria (una
  // sola carga por familia); si algún día el endpoint pagina en servidor, este mismo
  // centinela es el que dispararía el fetch de la página siguiente.
  const sentinelRef = useRef(null);

  const { data, isLoading, isError } = useQuery(['product-history', productId], () =>
    getProductHistory(productId).then((r) => r.data),
  );

  const grid = useMemo(
    () => ({
      display: 'grid',
      gridTemplateColumns: `64px 96px minmax(0, 1fr) repeat(${variants.length}, 54px) 72px`,
      alignItems: 'center',
    }),
    [variants.length],
  );

  // Meses tras aplicar el filtro de tipos: se ocultan los eventos filtrados y los
  // meses que se quedan vacíos. Los saldos no se recalculan: son el acumulado global
  // real tras cada movimiento, se muestre o no lo que hay entre medias.
  const filteredMonths = useMemo(() => {
    if (!data) return [];
    const visibleTypes = new Set(TYPE_FILTERS.filter((f) => activeFilters[f.key]).flatMap((f) => f.types));
    return data.months
      .map((m) => ({ ...m, events: m.events.filter((ev) => visibleTypes.has(ev.type)) }))
      .filter((m) => m.events.length > 0);
  }, [data, activeFilters]);

  const totalMonths = filteredMonths.length;

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || monthsVisible >= totalMonths) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting))
          setMonthsVisible((n) => Math.min(n + MONTHS_VISIBLE_STEP, totalMonths));
      },
      // Dispara un poco antes de llegar al final, para que la revelación no se note.
      { rootMargin: '300px 0px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [monthsVisible, totalMonths]);

  if (isLoading)
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
        <Spinner accessibilityLabel="Cargando historial" size="large" />
      </div>
    );

  if (isError || !data)
    return (
      <div style={{ padding: '1rem' }}>
        <Text color="critical">No se pudo cargar el historial.</Text>
      </div>
    );

  const { summary, saldoPerBarcode } = data;
  const months = filteredMonths;
  const visibleMonths = months.slice(0, monthsVisible);
  const soldPct = summary.arrived > 0 ? Math.round((summary.sold / summary.arrived) * 100) : 0;
  const lostNet = summary.found - summary.lost;

  const toggle = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <div>
      {/* Resumen del ciclo */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', borderBottom: '1px solid #e1e3e5' }}>
        {[
          { label: 'Llegadas', value: summary.arrived, sub: 'unidades recibidas' },
          { label: 'Vendidas', value: summary.sold, sub: `${soldPct} % de lo llegado` },
          {
            label: 'Extravío neto',
            value: lostNet,
            sub: `${summary.lost} perdidas · ${summary.found} aparecidas`,
            critical: lostNet < 0,
          },
          { label: 'Stock actual', value: summary.stock, sub: `en ${summary.stockDeposits} depósitos` },
        ].map((s) => (
          <div key={s.label} style={{ padding: '14px 18px', borderRight: '1px solid #f1f2f3' }}>
            <div style={{ fontSize: 12, color: '#6d7175' }}>{s.label}</div>
            <div style={{ fontSize: 20, fontWeight: 600, color: s.critical ? '#d72c0d' : '#202223' }}>{s.value}</div>
            <div style={{ fontSize: 11, color: '#8c9196' }}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Filtro de tipos de movimiento */}
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6, padding: '10px 16px', borderBottom: '1px solid #e1e3e5' }}>
        {TYPE_FILTERS.map((f) => {
          const active = activeFilters[f.key];
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => setActiveFilters((prev) => ({ ...prev, [f.key]: !prev[f.key] }))}
              style={{
                fontSize: 11,
                lineHeight: '14px',
                padding: '2px 9px',
                borderRadius: 10,
                fontWeight: 500,
                cursor: 'pointer',
                background: active ? f.color : '#ffffff',
                color: active ? '#202223' : '#8c9196',
                border: active ? '1px solid transparent' : '1px solid #c9cccf',
              }}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      {/* Cabecera de tabla */}
      <div style={{ ...grid, padding: '8px 16px', borderBottom: '1px solid #e1e3e5' }}>
        <Text variant="headingXs">Fecha</Text>
        <span />
        <Text variant="headingXs">Movimiento</Text>
        {variants.map((v) => (
          <Text key={v.barcode} variant="headingXs" alignment="center">
            {v.title}
          </Text>
        ))}
        <Text variant="headingXs" alignment="center">
          TOTAL
        </Text>
      </div>

      {/* Saldo actual, fijo arriba: el listado va de reciente a antiguo */}
      <div style={{ ...grid, padding: '9px 16px', background: '#fafbfb', borderBottom: '1px solid #e1e3e5' }}>
        <span />
        <span />
        <Text variant="headingXs">SALDO ACTUAL</Text>
        {variants.map((v) => (
          <div key={v.barcode} style={{ textAlign: 'center', fontSize: 13, fontWeight: 600 }}>
            {saldoPerBarcode[v.barcode] ?? 0}
          </div>
        ))}
        <div style={{ textAlign: 'center', fontSize: 14, fontWeight: 600 }}>
          {Object.values(saldoPerBarcode).reduce((a, b) => a + b, 0)}
        </div>
      </div>

      {visibleMonths.map((month) => (
        <div key={month.key}>
          <div style={{ padding: '5px 16px', background: '#f6f6f7', borderBottom: '1px solid #e1e3e5' }}>
            <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.6px', color: '#6d7175' }}>
              {monthLabel(month.key)}
            </span>
          </div>

          {month.events.map((ev, i) => {
            const key = `${month.key}-${i}`;
            const hasDetail = ev.detail && ev.detail.length > 0;
            const isOpen = !!expanded[key];
            return (
              <div key={key}>
                <div style={{ ...grid, padding: '7px 16px', borderBottom: '1px solid #f1f2f3' }}>
                  <span style={{ fontSize: 12, color: '#6d7175' }}>{dayLabel(ev)}</span>
                  {/* El badge tiene columna propia: todos alineados, con o sin chevron. */}
                  <span>
                    <Badge status={BADGE_STATUS[ev.type]}>{BADGE_LABEL[ev.type]}</Badge>
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    {hasDetail && (
                      <Button plain onClick={() => toggle(key)} icon={<Icon source={isOpen ? ChevronDownMinor : ChevronRightMinor} />} />
                    )}
                    <span style={{ fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {eventUrl(ev) ? <Link url={eventUrl(ev)}>{ev.label}</Link> : ev.label}
                    </span>
                    <span style={{ fontSize: 12, color: '#8c9196', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {ev.context}
                    </span>
                  </div>
                  {variants.map((v) => (
                    <MoveCell
                      key={v.barcode}
                      qty={ev.perBarcode[v.barcode]}
                      saldo={ev.neutral ? undefined : ev.saldoPerBarcode?.[v.barcode] ?? 0}
                      neutral={ev.neutral}
                    />
                  ))}
                  <MoveCell qty={ev.total} saldo={ev.neutral ? undefined : ev.saldoTotal} neutral={ev.neutral} />
                </div>

                {isOpen &&
                  ev.detail.map((det) => (
                    <div
                      key={`${key}-${det.refId}-${det.label}`}
                      style={{ ...grid, padding: '4px 16px', borderBottom: '1px solid #f6f6f7' }}
                    >
                      <span />
                      <span />
                      <span style={{ fontSize: 12, color: '#6d7175', paddingLeft: 8 }}>
                        {det.refId && (ev.type === 'TRANSFER_GROUP' || ev.type === 'RETURN') ? (
                          <Link url={`/transfers/${det.refId}`}>{`#${det.refId} ${det.label}`}</Link>
                        ) : (
                          det.label
                        )}
                      </span>
                      {variants.map((v) => (
                        <div key={v.barcode} style={{ textAlign: 'center', fontSize: 12, color: '#6d7175' }}>
                          {num(det.perBarcode[v.barcode]) === null ? '' : Math.abs(det.perBarcode[v.barcode])}
                        </div>
                      ))}
                      <div style={{ textAlign: 'center', fontSize: 12, color: '#6d7175' }}>{Math.abs(det.total)}</div>
                    </div>
                  ))}
              </div>
            );
          })}
        </div>
      ))}

      <div
        ref={sentinelRef}
        style={{ display: 'flex', justifyContent: 'center', gap: 10, padding: '12px 16px', background: '#fafbfb' }}
      >
        {monthsVisible < months.length ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#8c9196' }}>
            <Spinner accessibilityLabel="Cargando meses anteriores" size="small" />
            Cargando meses anteriores…
          </span>
        ) : (
          <span style={{ fontSize: 12, color: '#8c9196' }}>
            Inicio del historial{months.length ? ` · ${monthLabel(months[months.length - 1].key)}` : ''}
          </span>
        )}
      </div>
    </div>
  );
};

export default ProductHistoryTimeline;
